// src/pages/CompareCars.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { BiChevronLeft } from "react-icons/bi";
import LoadingSpinner from "../components/LoadingSpinner";
import { getCarById } from "../api/cars";

// Helper: turn camelCase or snake_case → "Camel Case"
function prettyKey(str) {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase());
}

// Renders three side‐by‐side tables: Base data, Extended data, and Pricing
function CompareOverview({
  baseA,
  extA,
  /* pricing props… */
  baseB,
  extB,
  /* pricing props… */
}) {
  // 1) Define which keys you want to hide
  const excludeBase = new Set([
    "id",
    "images",
    "regDate",
    "estP11D",
    "createdAt",
    "updatedAt",
    "modelDescription",
    "extras",
    "interiorFeatures",
    "status"
    // add any other base-keys you don’t want shown…
  ]);

  const excludeExt = new Set([
    "vin",
    "vrm",
    "isSorn",
    "extMake",
    "extModel",
    // add any other extended-keys you don’t want shown…
  ]);

  // 2) Build the final list of keys to render
  const baseFields = Object.keys(baseA).filter(
    (k) =>
      baseA[k] != null && // skip null/undefined
      typeof baseA[k] !== "object" && // skip arrays/objects
      !excludeBase.has(k) // skip excluded keys
  );

  const extFields = Object.keys(extA).filter(
    (k) => extA[k] != null && typeof extA[k] !== "object" && !excludeExt.has(k)
  );

  return (
    <section className="space-y-10">
      {/* Base Details */}
      <div>
        <h3 className="text-2xl font-semibold mb-4">Base Details</h3>
        <dl className="border rounded-lg overflow-hidden text-sm">
          {baseFields.map((key) => (
            <div
              key={key}
              className="grid grid-cols-3 py-2 px-3 border-b last:border-b-0 border-secondary-accent"
            >
              <dt className="font-medium text-secondary">{prettyKey(key)}</dt>
              <dd className="col-span-2 flex border-l border-secondary-accent">
                <div className="w-1/2 px-2">{String(baseA[key])}</div>
                <div className="w-1/2 px-2 border-l border-secondary-accent">
                  {String(baseB[key])}
                </div>
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Extended Details */}
      <div>
        <h3 className="text-2xl font-semibold mb-4">Extended Details</h3>
        <dl className="border rounded-lg overflow-hidden text-sm">
          {extFields.map((key) => (
            <div
              key={key}
              className="grid grid-cols-3 py-2 px-3 border-b last:border-b-0 border-secondary-accent"
            >
              <dt className="font-medium text-secondary">{prettyKey(key)}</dt>
              <dd className="col-span-2 flex border-l border-secondary-accent">
                <div className="w-1/2 px-2">{String(extA[key])}</div>
                <div className="w-1/2 px-2 border-l border-secondary-accent">
                  {String(extB[key])}
                </div>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
// function CompareOverview({
//   baseA,
//   extA,
//   rateExVATA,
//   vatChargeA,
//   baseB,
//   extB,
//   rateExVATB,
//   vatChargeB,
// }) {
//   // pick only scalar fields (skip arrays & objects)
//   const baseFields = Object.keys(baseA).filter(
//     (k) => baseA[k] != null && typeof baseA[k] !== "object"
//   );
//   const extFields = Object.keys(extA).filter(
//     (k) => extA[k] != null && typeof extA[k] !== "object"
//   );

//   return (
//     <section className="space-y-10">
//       {/* Base Details */}
//       <div>
//         <h3 className="text-2xl font-semibold mb-4">Base Details</h3>
//         <dl className="border rounded-lg overflow-hidden text-sm">
//           {baseFields.map((key) => (
//             <div
//               key={key}
//               className="grid grid-cols-3 py-2 px-3 border-b last:border-b-0 border-secondary-accent"
//             >
//               <dt className="font-medium text-secondary">{prettyKey(key)}</dt>
//               <dd className="col-span-2 flex border-l border-secondary-accent">
//                 <div className="w-1/2 px-2">{String(baseA[key])}</div>
//                 <div className="w-1/2 px-2 border-l border-secondary-accent">
//                   {String(baseB[key])}
//                 </div>
//               </dd>
//             </div>
//           ))}
//         </dl>
//       </div>

//       {/* Extended Details */}
//       <div>
//         <h3 className="text-2xl font-semibold mb-4">Extended Details</h3>
//         <dl className="border rounded-lg overflow-hidden text-sm">
//           {extFields.map((key) => (
//             <div
//               key={key}
//               className="grid grid-cols-3 py-2 px-3 border-b last:border-b-0 border-secondary-accent"
//             >
//               <dt className="font-medium text-secondary">{prettyKey(key)}</dt>
//               <dd className="col-span-2 flex border-l border-secondary-accent">
//                 <div className="w-1/2 px-2">{String(extA[key])}</div>
//                 <div className="w-1/2 px-2 border-l border-secondary-accent">
//                   {String(extB[key])}
//                 </div>
//               </dd>
//             </div>
//           ))}
//         </dl>
//       </div>
//     </section>
//   );
// }

export default function CompareCars() {
  const [searchParams] = useSearchParams();
  const id1 = searchParams.get("car1");
  const id2 = searchParams.get("car2");

  const [carA, setCarA] = useState(null);
  const [carB, setCarB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();

  useEffect(() => {
    if (!id1 || !id2) {
      setError("Two car IDs are required to compare.");
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([getCarById(id1), getCarById(id2)])
      .then(([a, b]) => {
        setCarA(a);
        setCarB(b);
      })
      .catch(() => setError("Failed to load car data."))
      .finally(() => setLoading(false));
  }, [id1, id2]);

  if (loading) return <LoadingSpinner />;
  if (error)
    return (
      <div className="text-center py-8">
        <p className="text-danger">{error}</p>
        <Link
          to="/used-cars"
          className="text-accent hover:underline inline-flex items-center mt-4"
        >
          <BiChevronLeft className="mr-1" /> Back to results
        </Link>
      </div>
    );
  if (!carA || !carB)
    return (
      <div className="text-center py-8">
        <p className="text-danger">One of the cars could not be found.</p>
        <Link
          to="/used-cars"
          className="text-accent hover:underline inline-flex items-center mt-4"
        >
          <BiChevronLeft className="mr-1" /> Back to results
        </Link>
      </div>
    );

  // destructure exactly what your API returns
  const {
    base: baseA,
    extended: extA,
    rateExVAT: rateExVATA,
    vatCharge: vatChargeA,
  } = carA;
  const {
    base: baseB,
    extended: extB,
    rateExVAT: rateExVATB,
    vatCharge: vatChargeB,
  } = carB;

  return (
    <div className="container mx-auto px-4 py-32 space-y-10 bg-primary pb-80 -mb-80">
      <Link
        to="/used-cars"
        className="inline-block text-accent hover:underline"
      >
        ← Back to results
      </Link>

      <h2 className="text-3xl font-bold text-on-surface">Compare Cars</h2>

      {/* Header cards */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {[
          { base: baseA, ext: extA },
          { base: baseB, ext: extB },
        ].map(({ base, ext }) => (
          <div
            key={base.id}
            className="border rounded-lg overflow-hidden shadow"
          >
            <div className="w-full aspect-[4/3] overflow-hidden bg-surface">
              <img
                src={base.images?.[0]?.url || "/placeholder.jpg"}
                alt={`${ext.make} ${ext.model}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h3 className="text-xl font-semibold">
                {ext.make} {ext.extModel || ext.model}
              </h3>
              <p className="text-secondary">{ext.year}</p>
              <p className="text-2xl font-bold text-accent">
                {base.price
                  ? `Price: £${base?.price.toLocaleString()}`
                  : "Price: £0"}

                {/* {base.estP11D != null
                  ? `P11D: £${base.estP11D.toLocaleString()}`
                  : "—"} */}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Side-by-side overview */}
      <CompareOverview
        baseA={baseA}
        extA={extA}
        rateExVATA={rateExVATA}
        vatChargeA={vatChargeA}
        baseB={baseB}
        extB={extB}
        rateExVATB={rateExVATB}
        vatChargeB={vatChargeB}
      />
    </div>
  );
}

// // src/pages/CompareCars.jsx
// import React, { useEffect, useState } from "react";
// import { useSearchParams, Link } from "react-router-dom";
// import { getCarById } from "../api/cars";
// import { BiChevronLeft } from "react-icons/bi";
// import LoadingSpinner from "../components/LoadingSpinner";

// // Helper to turn camelCase → "Camel Case"
// function prettyKey(str) {
//   return str.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
// }

// function CompareOverview({ carA, carB }) {
//   // pull in the new VehicleSmart payload
//   const vsA = carA.vehicleSmartData || {};
//   const vsB = carB.vehicleSmartData || {};
//   const dA = vsA.vehicleDetails || {};
//   const dB = vsB.vehicleDetails || {};
//   const calcMotA = vsA.calculatedMot || {};
//   const calcMotB = vsB.calculatedMot || {};
//   const taxA = vsA.calculatedTax || {};
//   const taxB = vsB.calculatedTax || {};

//   // each row now reads off `dX` (vehicleDetails) or the calculators
//   const sections = [
//     {
//       title: "Mechanical",
//       rows: [
//         {
//           label: "Engine",
//           getValue: (d) =>
//             d.cylinderCapacity != null
//               ? `${(d.cylinderCapacity / 1000).toFixed(1)}L`
//               : "-",
//         },
//         {
//           label: "Fuel",
//           getValue: (d) => d.fuel || "-",
//         },
//         {
//           label: "Revenue Weight",
//           getValue: (d) =>
//             d.revenueWeight != null ? `${d.revenueWeight} kg` : "-",
//         },
//         {
//           label: "Wheel Plan",
//           getValue: (d) => d.wheelplan || "-",
//         },
//       ],
//     },

//     {
//       title: "Trim",
//       rows: [
//         {
//           label: "Colour",
//           getValue: (_, car) => car.color || "-",
//         },
//         {
//           label: "Type Approval",
//           getValue: (d) => d.vehicleTypeApproval || "-",
//         },
//       ],
//     },

//     {
//       title: "Emissions & Economy",
//       rows: [
//         {
//           label: "CO₂ (g/km)",
//           getValue: (d) => d.co2Emissions || "-",
//         },
//         {
//           label: "Real Driving Emissions",
//           getValue: (d) => d.realDrivingEmissions || "-",
//         },
//         {
//           label: "Euro Status",
//           getValue: (d) => d.euroStatus || "-",
//         },
//       ],
//     },

//     {
//       title: "Useful Information",
//       rows: [
//         {
//           label: "Registration",
//           getValue: (_, car) => car.registration || "-",
//         },
//         {
//           label: "Year",
//           getValue: (d) => (d.year != null ? String(d.year) : "-"),
//         },
//         {
//           label: "Last V5C Issue Date",
//           getValue: (d) =>
//             d.lastV5CIssued
//               ? new Date(d.lastV5CIssued).toLocaleDateString()
//               : "-",
//         },
//       ],
//     },

//     {
//       title: "MOT & Compliance",
//       rows: [
//         {
//           label: "MOT Status",
//           getValue: (d) => d.rawDvlaMotStatus || "-",
//         },
//         {
//           label: "Marked For Export",
//           getValue: (d) => d.exportMarker || "-",
//         },
//       ],
//     },

//     {
//       title: "Taxation & Statutory",
//       rows: [
//         {
//           label: "Tax Status",
//           getValue: (d) => (d.taxed ? "Taxed" : "Untaxed"),
//         },
//         {
//           label: "Tax Due Date",
//           getValue: (d) =>
//             d.taxDueDate ? new Date(d.taxDueDate).toLocaleDateString() : "-",
//         },
//         {
//           label: "MOT Due Date",
//           getValue: (calc) =>
//             calc.motDate ? new Date(calc.motDate).toLocaleDateString() : "-",
//         },
//       ],
//     },
//   ];

//   return (
//     <section className="space-y-12">
//       {sections.map((sec) => (
//         <div key={sec.title}>
//           <h3 className="text-2xl font-semibold mb-4">{sec.title}</h3>
//           <dl className="border rounded-lg overflow-hidden text-sm">
//             {sec.rows.map(({ label, getValue }) => {
//               const valA = getValue(dA, carA, calcMotA, taxA);
//               const valB = getValue(dB, carB, calcMotB, taxB);
//               return (
//                 <div
//                   key={label}
//                   className="grid grid-cols-3 py-2 px-3 border-b last:border-b-0 border-secondary-accent"
//                 >
//                   <dt className="font-medium text-secondary">{label}</dt>
//                   <dd className="col-span-2 flex border-l border-secondary-accent">
//                     <div className="w-1/2 px-2">{valA}</div>
//                     <div className="w-1/2 px-2 border-l border-secondary-accent">
//                       {valB}
//                     </div>
//                   </dd>
//                 </div>
//               );
//             })}
//           </dl>
//         </div>
//       ))}
//     </section>
//   );
// }

// export default function CompareCars() {
//   const [searchParams] = useSearchParams();
//   const id1 = searchParams.get("car1");
//   const id2 = searchParams.get("car2");

//   const [carA, setCarA] = useState(null);
//   const [carB, setCarB] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState();

//   useEffect(() => {
//     if (!id1 || !id2) {
//       setError("Two car IDs are required to compare.");
//       setLoading(false);
//       return;
//     }
//     setLoading(true);
//     Promise.all([getCarById(id1), getCarById(id2)])
//       .then(([a, b]) => {
//         setCarA(a);
//         setCarB(b);
//       })
//       .catch(() => setError("Failed to load car data."))
//       .finally(() => setLoading(false));
//   }, [id1, id2]);

//   if (loading) return <LoadingSpinner />;
//   if (error)
//     return (
//       <div className="text-center py-8">
//         <p className="text-danger">{error}</p>
//         <Link
//           to="/used-cars"
//           className="text-accent hover:underline inline-flex items-center mt-4"
//         >
//           <BiChevronLeft className="mr-1" /> Back to results
//         </Link>
//       </div>
//     );
//   if (!carA || !carB)
//     return (
//       <div className="text-center py-8">
//         <p className="text-danger">One of the cars could not be found.</p>
//         <Link
//           to="/used-cars"
//           className="text-accent hover:underline inline-flex items-center mt-4"
//         >
//           <BiChevronLeft className="mr-1" /> Back to results
//         </Link>
//       </div>
//     );

//   // grab the same header fields you already do in CarDetail:
//   const vsA = carA.vehicleSmartData || {};
//   const vsB = carB.vehicleSmartData || {};
//   const dA = vsA.vehicleDetails || {};
//   const dB = vsB.vehicleDetails || {};

//   return (
//     <div className="container mx-auto px-4 py-32 space-y-10 bg-primary pb-80 -mb-80">
//       <Link
//         to="/used-cars"
//         className="inline-block text-accent hover:underline"
//       >
//         ← Back to results
//       </Link>

//       <h2 className="text-3xl font-bold text-on-surface">Compare Cars</h2>

//       {/* Header cards */}
//       <div className="grid grid-cols-2 gap-6 mb-8">
//         {[
//           { car: carA, d: dA },
//           { car: carB, d: dB },
//         ].map(({ car, d }) => (
//           <div
//             key={car.id}
//             className="border rounded-lg overflow-hidden shadow"
//           >
//             <div className="w-full aspect-[4/3] overflow-hidden bg-surface">
//               <img
//                 src={car.images?.[0]?.url || "/placeholder.jpg"}
//                 alt={`${d.make} ${d.model}`}
//                 className="w-full h-full object-cover"
//               />
//             </div>
//             <div className="p-4">
//               <h3 className="text-xl font-semibold">
//                 {d.make} {d.model}
//               </h3>
//               <p className="text-secondary">{d.year}</p>
//               <p className="text-2xl font-bold text-accent">
//                 {/* if you still have price: */}
//                 {car.price != null
//                   ? `£${car.price.toLocaleString()}`
//                   : car.estP11D != null
//                   ? `P11D: £${car.estP11D.toLocaleString()}`
//                   : "—"}
//               </p>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* Side-by-side overview */}
//       <CompareOverview carA={carA} carB={carB} />
//     </div>
//   );
// }

// // // src/pages/CompareCars.jsx
// // import React, { useEffect, useState } from "react";
// // import { useSearchParams, Link } from "react-router-dom";
// // import { getCarById } from "../api/cars";
// // import { BiChevronLeft } from "react-icons/bi";
// // import LoadingSpinner from "../components/LoadingSpinner";

// // // Helper to turn camelCase → "Camel Case"
// // function prettyKey(str) {
// //   return str.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
// // }

// // function CompareOverview({ carA, carB }) {
// //   const atA = carA.autotraderData || {};
// //   const atB = carB.autotraderData || {};

// //   // For each subsection, we list an array of { label, getValueA, getValueB }.
// //   // getValueX is a small function that reads the appropriate field(s) from atX or carX.
// //   const sections = [
// //     {
// //       title: "Mechanical",
// //       rows: [
// //         {
// //           label: "Engine",
// //           getValue: (at) =>
// //             at.badgeEngineSizeLitres
// //               ? `${at.badgeEngineSizeLitres}L`
// //               : at.engineCapacityCC
// //               ? `${(at.engineCapacityCC / 1000).toFixed(1)}L`
// //               : "-",
// //         },
// //         {
// //           label: "Fuel",
// //           getValue: (at) => at.fuelType || "-",
// //         },
// //         {
// //           label: "Revenue Weight",
// //           getValue: (_) => "1640", // hardcoded in your CarDetail; replace if dynamic
// //         },
// //         {
// //           label: "Wheel Plan",
// //           getValue: (_) => "NON STANDARD", // hardcoded; replace if you have real data
// //         },
// //       ],
// //     },

// //     {
// //       title: "Trim",
// //       rows: [
// //         {
// //           label: "Colour",
// //           getValue: (at, car) => at.colour || car.color || "-",
// //         },
// //         {
// //           label: "Doors",
// //           getValue: (at) => (at.doors != null ? String(at.doors) : "-"),
// //         },
// //         {
// //           label: "Seats",
// //           getValue: (at) => (at.seats != null ? String(at.seats) : "-"),
// //         },
// //         {
// //           label: "Type Approval",
// //           getValue: (_) => "N1", // hardcoded in CarDetail; replace if dynamic
// //         },
// //       ],
// //     },

// //     {
// //       title: "Emissions & Economy",
// //       rows: [
// //         {
// //           label: "CO₂ (g/km)",
// //           getValue: (at) =>
// //             at.co2EmissionGPKM != null ? String(at.co2EmissionGPKM) : "-",
// //         },
// //         {
// //           label: "Real Driving Emissions",
// //           getValue: (_) => "1", // hardcoded; replace if you have real data
// //         },
// //         {
// //           label: "Euro Status",
// //           getValue: (_) => "EURO 6 AD", // hardcoded; replace if dynamic
// //         },
// //       ],
// //     },

// //     {
// //       title: "Useful information",
// //       rows: [
// //         {
// //           label: "Vehicle registration",
// //           getValue: (at) => at.registration || "-",
// //         },
// //         {
// //           label: "Year",
// //           getValue: (at, car) => (car.year != null ? String(car.year) : "-"),
// //         },
// //         {
// //           label: "Last V5C Issue Date",
// //           getValue: (at) =>
// //             at.firstRegistrationDate
// //               ? new Date(at.firstRegistrationDate).toLocaleDateString()
// //               : "-",
// //           // In CarDetail you hardcode “2016-12-25”.
// //           // If you want that exact date, replace with a constant or at.firstRegistrationDate.
// //         },
// //         // …you can add “Previous owners,” “Insurance group,” etc.,
// //         //  if you uncomment those in CarDetail. For now, I’m matching what’s visible.
// //       ],
// //     },

// //     {
// //       title: "MOT & Compliance",
// //       rows: [
// //         {
// //           label: "MOT Status",
// //           getValue: (_) => "No details held by DVLA",
// //         },
// //         {
// //           label: "Marked For Export",
// //           getValue: (_) => "false",
// //         },
// //       ],
// //     },

// //     {
// //       title: "Taxation & Statutory",
// //       rows: [
// //         {
// //           label: "Tax Status",
// //           getValue: (_) => "Untaxed",
// //         },
// //         {
// //           label: "Tax Due Date",
// //           getValue: (_) => "2007-01-01", // from CarDetail
// //         },
// //         {
// //           label: "ART End Date",
// //           getValue: (_) => "2025-02-28", // from CarDetail
// //         },
// //       ],
// //     },
// //   ];

// //   return (
// //     <section className="space-y-12">
// //       {sections.map((section) => (
// //         <div key={section.title}>
// //           <h3 className="text-2xl font-semibold mb-4">{section.title}</h3>
// //           <dl className="border rounded-lg overflow-hidden text-sm">
// //             {section.rows.map(({ label, getValue }) => {
// //               // Compute each car’s value (falling back to "-" if null)
// //               const valA = getValue(atA, carA);
// //               const valB = getValue(atB, carB);

// //               return (
// //                 <div
// //                   key={label}
// //                   className="grid grid-cols-3 py-2 px-3 border-b last:border-b-0 border-secondary-accent"
// //                 >
// //                   {/* Column 1: Label */}
// //                   <dt className="font-medium text-secondary">{label}</dt>

// //                   {/* Columns 2 & 3: Car A and Car B */}
// //                   <dd className="col-span-2 flex border-l border-secondary-accent">
// //                     <div className="w-1/2 px-2">{valA || "-"}</div>
// //                     <div className="w-1/2 px-2 border-l border-secondary-accent">
// //                       {valB || "-"}
// //                     </div>
// //                   </dd>
// //                 </div>
// //               );
// //             })}
// //           </dl>
// //         </div>
// //       ))}
// //     </section>
// //   );
// // }

// // export default function CompareCars() {
// //   const [searchParams] = useSearchParams();
// //   const id1 = searchParams.get("car1");
// //   const id2 = searchParams.get("car2");

// //   const [carA, setCarA] = useState(null);
// //   const [carB, setCarB] = useState(null);
// //   const [loading, setLoading] = useState(true);
// //   const [error, setError] = useState();

// //   useEffect(() => {
// //     if (!id1 || !id2) {
// //       setError("Two car IDs are required to compare.");
// //       setLoading(false);
// //       return;
// //     }
// //     setLoading(true);
// //     setError(null);

// //     // Fetch both cars in parallel
// //     Promise.all([getCarById(id1), getCarById(id2)])
// //       .then(([dataA, dataB]) => {
// //         setCarA(dataA);
// //         setCarB(dataB);
// //       })
// //       .catch((err) => {
// //         console.error(err);
// //         setError("Failed to load car data.");
// //       })
// //       .finally(() => setLoading(false));
// //   }, [id1, id2]);

// //   if (loading) {
// //     return <LoadingSpinner />;
// //   }

// //   if (error) {
// //     return (
// //       <div className="text-center py-8">
// //         <p className="text-danger">{error}</p>
// //         <Link
// //           to="/used-cars"
// //           className="text-accent hover:underline inline-flex items-center mt-4"
// //         >
// //           <BiChevronLeft className="mr-1" />
// //           Back to results
// //         </Link>
// //       </div>
// //     );
// //   }

// //   if (!carA || !carB) {
// //     return (
// //       <div className="text-center py-8">
// //         <p className="text-danger">One of the cars could not be found.</p>
// //         <Link
// //           to="/used-cars"
// //           className="text-accent hover:underline inline-flex items-center mt-4"
// //         >
// //           <BiChevronLeft className="mr-1" />
// //           Back to results
// //         </Link>
// //       </div>
// //     );
// //   }

// //   return (
// //     <div className="container mx-auto px-4 py-32 space-y-10 bg-primary pb-80 -mb-80">
// //       <Link
// //         to="/used-cars"
// //         className="inline-block text-accent hover:underline"
// //       >
// //         ← Back to results
// //       </Link>

// //       <h2 className="text-3xl font-bold text-on-surface">Compare Cars</h2>

// //       {/* Basic header with thumbnails + price for each car */}
// //       <div className="grid grid-cols-2 gap-6 mb-8">
// //         {[carA, carB].map((car) => (
// //           <div
// //             key={car.id}
// //             className="border rounded-lg overflow-hidden shadow"
// //           >
// //             <div className="w-full aspect-[4/3] overflow-hidden bg-surface">
// //               <img
// //                 src={car.images[0].url || "/placeholder.jpg"}
// //                 alt={`${car.make} ${car.model}`}
// //                 className="w-full h-full object-cover"
// //               />
// //             </div>
// //             <div className="p-4">
// //               <h3 className="text-xl font-semibold">
// //                 {car.make} {car.model}
// //               </h3>
// //               <p className="text-secondary">{car.year}</p>
// //               <p className="text-2xl font-bold text-accent">
// //                 £{car?.price ? car.price.toLocaleString() : " No price"}
// //               </p>
// //             </div>
// //           </div>
// //         ))}
// //       </div>

// //       {/* Now render Car Overview section side-by-side */}
// //       <CompareOverview carA={carA} carB={carB} />
// //     </div>
// //   );
// // }
